<template>
  <VaCollapse
    v-if="hasChildren && depth < 3"
    v-model="isOpen"
    class="gc-collapse"
  >
    <template #header>
      <div
        class="gc-item flex items-center justify-between"
        :class="[`depth-${depth}`, { active: isActive }]"
        @click.stop="toggleOpen"
      >
        <div class="gc-left flex items-center gap-2 w-full">
          <VaIcon
            v-if="depth === 1 && route.meta?.icon"
            :name="route.meta.icon"
            size="20px"
            :color="isActive ? 'primary' : 'secondary'"
          />
          <span class="gc-label" :class="textClass">{{ label }}</span>
        </div>
        <VaIcon
          :name="isOpen ? 'va-arrow-up' : 'va-arrow-down'"
          size="18px"
          color="secondary"
        />
      </div>
    </template>

    <template #body>
      <div class="gc-children">
        <RecursiveSidebarItem
          v-for="(child, i) in route.children"
          :key="i"
          :route="child"
          :depth="depth + 1"
        />
      </div>
    </template>
  </VaCollapse>

  <!-- leaf -->
  <div
    v-else
    class="gc-item flex items-center justify-between"
    :class="[`depth-${depth}`, { active: isActive }]"
    @click="navigate"
  >
    <div class="gc-left flex items-center gap-2 w-full">
      <VaIcon
        v-if="depth === 1 && route.meta?.icon"
        :name="route.meta.icon"
        size="20px"
        :color="isActive ? 'primary' : 'secondary'"
      />
      <span class="gc-label" :class="textClass">{{ label }}</span>
    </div>
    <div class="gc-arrow-placeholder"></div>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import RecursiveSidebarItem from './RecursiveSidebarItem.vue'

const props = defineProps({
  route: { type: Object, required: true },
  depth: { type: Number, default: 1 },
})
const router = useRouter()
const current = useRoute()
const { t, te } = useI18n()

const hasChildren = computed(() => Array.isArray(props.route.children) && props.route.children.length > 0)
const rawKey = computed(() => String(props.route.displayName ?? props.route.name ?? ''))
const label = computed(() => (te(rawKey.value) ? t(rawKey.value) : rawKey.value))
const isActive = computed(() => current.name === props.route.name)
const isOpen = ref(false)

watchEffect(() => {
  if (isActive.value) isOpen.value = true
})

const toggleOpen = () => (isOpen.value = !isOpen.value)
const navigate = () => {
  if (props.route?.name) router.push({ name: props.route.name }).catch(() => {})
}

const textClass = computed(() => {
  if (props.depth === 1) return 'text-base font-semibold'
  if (props.depth === 2) return 'text-sm font-medium'
  return 'text-sm text-secondary'
})
</script>

<style scoped>
.gc-item {
  cursor: pointer;
  user-select: none;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
  padding: 10px 8px;
}

.gc-item.active {
  border-left-color: var(--va-primary);
  background: rgba(var(--va-primary-rgb), 0.08);
}
.gc-item:hover {
  background: rgba(var(--va-on-background-rgb), 0.05);
}

/* 🔹 depth별 들여쓰기 (Collapse header와 leaf 동일하게 보이도록) */
.depth-1.gc-item { padding-left: 1.25rem; } /* 20px */
.depth-2.gc-item { padding-left: 2.25rem; } /* 36px */
.depth-3.gc-item { padding-left: 3.25rem; } /* 52px */

/* 🔹 Vuestic Collapse header wrapper 기본 margin 제거 */
:deep(.va-collapse__header) {
  padding-left: 0 !important;
  margin-left: 0 !important;
}

/* 🔹 children 컨테이너는 depth 계승 */
.gc-children {
  margin-left: 0;
  padding-left: 0;
  border-left: 1px solid rgba(var(--va-on-background-rgb), 0.06);
}

.gc-label {
  flex: 1;
  line-height: 1.4;
}

.gc-arrow-placeholder {
  width: 18px;
  height: 18px;
  visibility: hidden;
}
</style>
